import type { CheckoutLanguage } from '../components/checkoutLabels';
import { useCommerce } from './ShopCommerce';

// The names a screen reader hears for the storefront's own controls — arrows, the remove cross, the breadcrumb, the
// loading lists — and the order note's hint, in the language the store is served in, as the visible words already are.
// `{label}`, `{title}`, `{names}`, `{n}` and `{count}` are filled in by `named`.

/** Every name the storefront gives a control of its own. */
export type ShopNames = {
  previous: string; next: string; slides: string; slideOf: string; breadcrumb: string; showMenu: string;
  quickView: string; remove: string; add: string; loadingCart: string; loadingOffer: string; searching: string;
  chooseTab: string; colours: string; noteHint: string; hideMenu: string; menuOf: string; closeMenu: string;
  onNetwork: string; ourStore: string;
};

const NAMES: Readonly<Record<CheckoutLanguage, ShopNames>> = {
  en: {
    previous: 'Previous', next: 'Next', slides: 'Slides', slideOf: 'Slide {n} of {count}', breadcrumb: 'Breadcrumb',
    showMenu: 'Show the {label} menu', quickView: 'Quick view: {title}', remove: 'Remove {title}', add: 'Add {title}',
    loadingCart: 'Loading your cart', loadingOffer: 'Loading your offer', searching: 'Searching', chooseTab: 'Choose a tab',
    colours: 'Colours: {names}', noteHint: 'How can we help?', hideMenu: 'Hide the {label} menu', menuOf: '{title} menu',
    closeMenu: 'Close the menu', onNetwork: '{store} on {network}', ourStore: 'Our store'
  },
  fr: {
    previous: 'Précédent', next: 'Suivant', slides: 'Diapositives', slideOf: 'Diapositive {n} sur {count}', breadcrumb: 'Fil d’Ariane',
    showMenu: 'Afficher le menu {label}', quickView: 'Aperçu rapide : {title}', remove: 'Retirer {title}', add: 'Ajouter {title}',
    loadingCart: 'Chargement de votre panier', loadingOffer: 'Chargement de votre offre', searching: 'Recherche en cours', chooseTab: 'Choisir un onglet',
    colours: 'Couleurs : {names}', noteHint: 'Une précision pour votre commande ?', hideMenu: 'Masquer le menu {label}', menuOf: 'Menu {title}',
    closeMenu: 'Fermer le menu', onNetwork: '{store} sur {network}', ourStore: 'Notre boutique'
  },
  ar: {
    previous: 'السابق', next: 'التالي', slides: 'الشرائح', slideOf: 'الشريحة {n} من {count}', breadcrumb: 'مسار التنقل',
    showMenu: 'عرض قائمة {label}', quickView: 'نظرة سريعة: {title}', remove: 'إزالة {title}', add: 'إضافة {title}',
    loadingCart: 'جارٍ تحميل سلتك', loadingOffer: 'جارٍ تحميل عرضك', searching: 'جارٍ البحث', chooseTab: 'اختر تبويبًا',
    colours: 'الألوان: {names}', noteHint: 'هل من ملاحظة على طلبك؟', hideMenu: 'إخفاء قائمة {label}', menuOf: 'قائمة {title}',
    closeMenu: 'إغلاق القائمة', onNetwork: '{store} على {network}', ourStore: 'متجرنا'
  }
};

// Picks a language's names, English for a store that has not chosen one.
export const shopNames = (language: CheckoutLanguage | null | undefined): ShopNames => NAMES[language ?? 'en'];

// Reads the names in the language of the store this visit is in.
export const useShopNames = (): ShopNames => shopNames(useCommerce()?.language);

// Fills a name's blanks: `named(names.remove, { title })` reads "Retirer Sabots …".
export const named = (phrase: string, values: Readonly<Record<string, string | number>>): string =>
  phrase.replace(/\{(\w+)\}/g, (blank, key: string) => (key in values ? String(values[key]) : blank));
